import { useState } from "react";

/**
 * The one and only look anybody gets at a generated password.
 *
 * IT CANNOT BE SHOWN AGAIN, and that is a property of the system rather than a
 * limitation of this dialog: only an Argon2id hash was stored, so there is
 * nothing left to show. There is deliberately no "show me again" and no place
 * this value is kept — it lives in this component's state until it closes, and
 * it is never put in a toast, a query cache, browser storage or a URL.
 *
 * So the copy says exactly that, before the operator dismisses it.
 */
export function TemporaryPasswordDialog({
  title,
  displayName,
  email,
  password,
  onClose,
}: {
  title: string;
  displayName: string;
  email: string;
  password: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      setCopyFailed(false);
    } catch {
      // A browser that refuses clipboard access is not an error worth an
      // alert — the password is on screen and can be selected by hand.
      setCopyFailed(true);
    }
  }

  return (
    <div className="dialog-backdrop" role="presentation">
      <div
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="temp-password-title"
      >
        <h2 className="dialog__title" id="temp-password-title">
          {title}
        </h2>
        <div className="dialog__body">
          <div className="stack">
            <p>
              <strong>{displayName}</strong> ({email}) can now sign in with this
              temporary password.
            </p>

            <div className="users__secret">
              <code className="users__secretValue" data-testid="temporary-password">
                {password}
              </code>
              <button type="button" className="btn btn--sm" onClick={() => void copy()}>
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            {copyFailed ? (
              <p className="muted">
                Copying was blocked by the browser — select the password above
                and copy it by hand.
              </p>
            ) : null}

            <div className="notice" role="alert">
              <p>
                <strong>Copy it now.</strong> This is the only time it is shown.
                It cannot be retrieved later — only a hash of it is stored — and
                if it is lost the password has to be reset again.
              </p>
              <p>
                Give it to them directly. They must change it the first time
                they sign in, and until they do they cannot use anything else in
                the dashboard.
              </p>
            </div>
          </div>
        </div>
        <div className="dialog__actions">
          <button type="button" className="btn btn--primary" onClick={onClose}>
            I have copied it
          </button>
        </div>
      </div>
    </div>
  );
}
